import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Eye, Upload, Trash2, Copy, Cpu, KeyRound, Terminal, ShieldCheck } from "lucide-react";
import {
  computeKey, findWriteConstants, findRecoveryConstants, detectMcu,
  parseSeed, keyHex, hex16, hexOff,
} from "../lib/shindengenKey";

export default function ShindengenSeedKey() {
  return <ShindengenSeedKeyInner />;
}

function ShindengenSeedKeyInner() {
  const inputRef = useRef(null);
  const [buf, setBuf] = useState(null);
  const [fileName, setFileName] = useState("");
  const [dragging, setDragging] = useState(false);
  const [seed, setSeed] = useState("1234");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".bin")) { toast.error("Please select a .bin ECU dump"); return; }
    const ab = await file.arrayBuffer();
    setBuf(ab); setFileName(file.name);
    toast.success(`Loaded ${file.name} — ${ab.byteLength} bytes`);
  };

  const analysis = useMemo(() => {
    if (!buf) return null;
    return {
      mcu: detectMcu(buf),
      write: findWriteConstants(buf),
      recovery: findRecoveryConstants(buf),
    };
  }, [buf]);

  const seedParsed = useMemo(() => parseSeed(seed), [seed]);

  const keys = useMemo(() => {
    if (!analysis || !seedParsed) return null;
    const s = seedParsed.seed16;
    return {
      write: analysis.write ? keyHex(computeKey(s, analysis.write.C0, analysis.write.C1, analysis.write.C2)) : null,
      recovery: analysis.recovery ? keyHex(computeKey(s, analysis.recovery.C0, analysis.recovery.C1, analysis.recovery.C2)) : null,
    };
  }, [analysis, seedParsed]);

  const console_ = useMemo(() => {
    if (!analysis) return [];
    const log = [];
    log.push([`Loaded ${buf.byteLength} bytes`, "ok"]);
    log.push([`MCU detected: ${analysis.mcu === "V850" ? "Renesas V850 (SV850)" : "unknown"}`, analysis.mcu === "V850" ? "ok" : "warn"]);
    if (analysis.write)
      log.push([`WRITE-key signature @${hexOff(analysis.write.sigOffset)} · constants @${hexOff(analysis.write.constOffset)}  C0=${hex16(analysis.write.C0)} C1=${hex16(analysis.write.C1)} C2=${hex16(analysis.write.C2)}`, "ok"]);
    else log.push(["WRITE-key signature not found in this dump", "err"]);
    if (analysis.recovery)
      log.push([`RECOVERY-key signature @${hexOff(analysis.recovery.sigOffset)} · constants @${hexOff(analysis.recovery.constOffset)}  C0=${hex16(analysis.recovery.C0)} C1=${hex16(analysis.recovery.C1)} C2=${hex16(analysis.recovery.C2)}`, "ok"]);
    else log.push(["RECOVERY-key signature not found in this dump", "warn"]);
    return log;
  }, [analysis, buf]);

  const copy = (v, label) => { if (!v) return; navigator.clipboard?.writeText(v); toast.success(`${label} copied`); };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><Eye className="h-4 w-4" /> Security Access</div>
        <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Seed Key View</h1>
        <p className="text-slate-500 text-sm mt-1">Shindengen ECU security access. Drop the <span className="text-cyan-300 font-mono-tt">.bin</span>, enter the <span className="text-cyan-300 font-mono-tt">seed</span> the ECU sends, and read the matching <span className="text-cyan-300 font-mono-tt">write &amp; recovery key</span>. Everything runs in your browser.</p>
      </div>

      {/* Dropzone */}
      <div
        data-testid="seedkey-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
        className={`rounded-xl border-2 border-dashed p-8 text-center transition-colors ${dragging ? "border-cyan-400 bg-cyan-400/5" : "border-slate-700 bg-[#0c1017]"}`}
      >
        <input ref={inputRef} data-testid="seedkey-file-input" type="file" accept=".bin" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
        <Upload className="h-8 w-8 text-slate-500 mx-auto mb-3" />
        <p className="text-slate-300 font-semibold">Drop your Shindengen <span className="font-mono-tt text-cyan-300">.bin</span> firmware here</p>
        <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
          <button data-testid="seedkey-browse-btn" onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold transition-colors">
            <Upload className="h-4 w-4" /> Browse .bin
          </button>
          {buf && (
            <button data-testid="seedkey-clear-btn" onClick={() => { setBuf(null); setFileName(""); if (inputRef.current) inputRef.current.value = ""; }}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold">
              <Trash2 className="h-4 w-4" /> Clear
            </button>
          )}
        </div>
        {fileName && <p className="mt-3 text-xs font-mono-tt text-slate-500">{fileName} · {buf?.byteLength} bytes</p>}
      </div>

      {buf && (
        <div className="grid lg:grid-cols-3 gap-5 mt-6">
          <div className="lg:col-span-2 space-y-5">
            {/* Seed + keys */}
            <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5">
              <h2 className="font-head font-bold text-xl uppercase text-white flex items-center gap-2 mb-4"><KeyRound className="h-5 w-5 text-cyan-400" /> Seed → Key</h2>

              <label className="text-[10px] uppercase tracking-widest text-slate-500">ECU Seed (hex, 2 bytes)</label>
              <input
                data-testid="seedkey-seed-input"
                value={seed}
                onChange={(e) => setSeed(e.target.value)}
                placeholder="e.g. 12 34"
                className="mt-1 w-full bg-[#121824] border border-slate-700 rounded-lg px-3 py-3 text-white font-mono-tt text-lg tracking-[0.2em] focus:outline-none focus:ring-1 focus:ring-cyan-400"
              />
              <p className="text-xs font-mono-tt text-slate-600 mt-1">
                seed16 = {seedParsed ? hex16(seedParsed.seed16) : "—"} ({seedParsed ? seedParsed.seed16 : "—"})
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <KeyCard testid="seedkey-write-result" label="Write Key" value={keys?.write} found={!!analysis.write} onCopy={() => copy(keys?.write, "Write key")} />
                <KeyCard testid="seedkey-recovery-result" label="Recovery Key" value={keys?.recovery} found={!!analysis.recovery} onCopy={() => copy(keys?.recovery, "Recovery key")} />
              </div>
            </div>

            {/* Console */}
            <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5">
              <h2 className="font-head font-bold text-lg uppercase text-white flex items-center gap-2 mb-3"><Terminal className="h-5 w-5 text-cyan-400" /> Firmware Analysis</h2>
              <div data-testid="seedkey-console" className="rounded-lg bg-[#07090e] border border-slate-800 p-3 font-mono-tt text-xs space-y-1.5">
                {console_.map(([msg, kind], i) => (
                  <div key={i} className={kind === "ok" ? "text-emerald-400" : kind === "warn" ? "text-amber-400" : "text-cyan-300"}>
                    <span className="text-slate-600">$ </span>{msg}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Constants panel */}
          <div className="rounded-xl border border-slate-800 bg-[#0c1017] p-5 self-start space-y-5">
            <div>
              <h2 className="font-head font-bold text-lg uppercase text-white flex items-center gap-2 mb-3"><Cpu className="h-5 w-5 text-cyan-400" /> Constants</h2>
              <ConstBlock title="Write" c={analysis.write} />
              <div className="h-3" />
              <ConstBlock title="Recovery" c={analysis.recovery} />
            </div>
            <div className="rounded-lg border border-slate-800 bg-[#07090e] p-3 text-xs text-slate-500 font-mono-tt leading-relaxed">
              <div className="flex items-center gap-1.5 text-slate-400 mb-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> How it works</div>
              key = C1·(seed+C0) &amp; 0xFFFF, then + (seed mod C2). Constants are read straight from your firmware, so the key is valid for this exact ECU.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const KeyCard = ({ testid, label, value, found, onCopy }) => (
  <div data-testid={testid} className={`rounded-lg border p-5 text-center ${found ? "border-red-700/50 bg-red-950/20" : "border-slate-800 bg-[#07090e]"}`}>
    <div className="text-[10px] uppercase tracking-[0.25em] text-cyan-300/80">{label}</div>
    {found ? (
      <>
        <div className="font-head font-black text-4xl text-white tracking-[0.25em] mt-2">{value}</div>
        <button onClick={onCopy} className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold text-xs">
          <Copy className="h-3.5 w-3.5" /> Copy
        </button>
      </>
    ) : (
      <div className="text-slate-600 font-mono-tt text-sm mt-3">signature not found</div>
    )}
  </div>
);

const ConstBlock = ({ title, c }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{title}</div>
    {c ? (
      <dl className="space-y-1 text-sm font-mono-tt">
        {[["offset", hexOff(c.constOffset)], ["C0", hex16(c.C0)], ["C1", hex16(c.C1)], ["C2", hex16(c.C2)]].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-slate-800/60 pb-1">
            <dt className="text-[10px] uppercase tracking-widest text-slate-500">{k}</dt>
            <dd className="text-slate-200">{v}</dd>
          </div>
        ))}
      </dl>
    ) : <p className="text-slate-600 text-xs font-mono-tt">not found</p>}
  </div>
);
